import type { JSX } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CompanyFormFields } from './CompanyFormFields';
import { CompanyLogoFields } from './CompanyLogoFields';
import { useEditCompanyForm } from './EditCompanyDialog.hooks';
import type { Company } from '../types/company.types';

interface EditCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company;
}

function SubmitButtons({
  isLoading,
  isUploading,
  onCancel,
}: {
  isLoading: boolean;
  isUploading: boolean;
  onCancel: () => void;
}): React.JSX.Element {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" disabled={isLoading || isUploading}>
        {isLoading ? 'Saving...' : isUploading ? 'Uploading...' : 'Save'}
      </Button>
    </DialogFooter>
  );
}

export function EditCompanyDialog({
  open,
  onOpenChange,
  company,
}: EditCompanyDialogProps): JSX.Element {
  const { form, ...state } = useEditCompanyForm(company, open, onOpenChange);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-row-click-ignore
        className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]"
      >
        <DialogHeader>
          <DialogTitle>Edit Company</DialogTitle>
        </DialogHeader>
        <form onSubmit={state.onSubmit} className="space-y-4">
          <CompanyFormFields
            register={form.register}
            errors={form.formState.errors}
            onSizeChange={(v) => form.setValue('companySize', v, { shouldDirty: true })}
            onCountryChange={(v) => form.setValue('country', v, { shouldDirty: true })}
            countryValue={form.watch('country') ?? company.country ?? ''}
            sizeValue={form.watch('companySize') || company.companySize}
          />
          <CompanyLogoFields
            logoFile={state.logoFile}
            darkLogoFile={state.darkLogoFile}
            onLogoChange={state.setLogoFile}
            onDarkLogoChange={state.setDarkLogoFile}
          />
          <SubmitButtons
            isLoading={state.isLoading}
            isUploading={state.isUploading}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}
